'use client'

import {useCallback, useEffect, useMemo, useState} from 'react'
import {useRouter} from '@/lib/router'
import {Loader2, MessageSquare, Plus, RefreshCw, Search, Tag as TagIcon, User, UserPlus, X} from 'lucide-react'
import type {Account, Contact} from '@/types'
import {avatarDisplaySrc, displayName, filterContacts} from '@/lib/utils'
import {apiPost} from '@/lib/api'
import {PageHeader} from '@/components/PageHeader'
import {LazyAvatar} from '@/components/LazyAvatar'
import {SexIcon} from '@/components/SexIcon'
import {useLabels} from '@/hooks/useLabels'
import {message} from '@/components/Toast'

type FriendSearchResult = {
    keyword: string
    wxid: string
    nickName?: string
    alias?: string
    avatar?: string
    sex?: string
    province?: string
    city?: string
    v3: string
    v4: string
    scene: number
}

export function AddressBookView({
    account,
    contacts,
    syncContacts,
    syncing,
}: {
    contacts: Contact[]
    account: Account | null
    syncContacts: (currentAccount?: Account | null) => Promise<void> | void
    syncing: boolean
}) {
    const router = useRouter()
    const [query, setQuery] = useState('')
    const [selectedWxid, setSelectedWxid] = useState('')
    const [addFriendOpen, setAddFriendOpen] = useState(false)
    const [friendKeyword, setFriendKeyword] = useState('')
    const [friendVerifyContent, setFriendVerifyContent] = useState('你好，我想添加你为好友')
    const [friendSearchResult, setFriendSearchResult] = useState<FriendSearchResult | null>(null)
    const [searchingFriend, setSearchingFriend] = useState(false)
    const [addingFriend, setAddingFriend] = useState(false)
    const filteredContacts = useMemo(() => filterContacts(contacts, query), [contacts, query])
    const selected = contacts.find((contact) => contact.wxid === selectedWxid) || contacts[0] || null

    useEffect(() => {
        if (!addFriendOpen) return
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !addingFriend && !searchingFriend) setAddFriendOpen(false)
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [addFriendOpen, addingFriend, searchingFriend])

    // Label cache — used to resolve label ids into names for the detail
    // panel. Lazy: only reads the local cache, the Labels page has the
    // refresh control.
    const {labels} = useLabels(account)
    const labelMap = useMemo(() => {
        const map = new Map<number, string>()
        for (const label of labels) map.set(label.id, label.name)
        return map
    }, [labels])

    const resolveLabelNames = (labelIds: string | undefined): {id: number; name: string}[] => {
        if (!labelIds) return []
        const names: {id: number; name: string}[] = []
        for (const raw of labelIds.split(',')) {
            const id = Number(raw.trim())
            if (!Number.isFinite(id) || id <= 0) continue
            const name = labelMap.get(id)
            if (name) names.push({id, name})
            else names.push({id, name: `#${id}`})
        }
        return names
    }

    const handleOpenConversation = useCallback((contact: Contact) => {
        if (account) {
            apiPost('/conversations/ensure', {wxid: account.wxid, conversationWxid: contact.wxid}, {silent: true}).catch(() => {})
        }
        router.push('/conversation/realtime?conversationWxid=' + encodeURIComponent(contact.wxid))
    }, [account, router])

    const openAddFriend = () => {
        if (!account) {
            message.warning('请先选择已登录的微信实例')
            return
        }
        setFriendKeyword('')
        setFriendVerifyContent('你好，我想添加你为好友')
        setFriendSearchResult(null)
        setAddFriendOpen(true)
    }

    const closeAddFriend = () => {
        if (addingFriend || searchingFriend) return
        setAddFriendOpen(false)
    }

    const handleSearchFriend = async () => {
        const keyword = friendKeyword.trim()
        if (!account) {
            message.warning('请先选择已登录的微信实例')
            return
        }
        if (!keyword) {
            message.warning('请输入微信号或手机号')
            return
        }
        setSearchingFriend(true)
        setFriendSearchResult(null)
        try {
            const response = await apiPost<{success: boolean; contact?: FriendSearchResult; message?: string}>('/friends/search', {
                wxid: account.wxid,
                keyword,
            }, {silent: true})
            if (response.success === false || !response.contact) {
                message.error(response.message || '未找到该用户')
                return
            }
            setFriendSearchResult(response.contact)
        } catch (error) {
            message.error(error instanceof Error ? error.message : '搜索用户失败')
        } finally {
            setSearchingFriend(false)
        }
    }

    const handleAddFriend = async () => {
        if (!account) {
            message.warning('请先选择已登录的微信实例')
            return
        }
        if (!friendSearchResult) {
            message.warning('请先搜索并选择要添加的用户')
            return
        }
        setAddingFriend(true)
        try {
            const response = await apiPost<{success: boolean; message?: string}>('/friends/add', {
                wxid: account.wxid,
                targetWxid: friendSearchResult.wxid,
                v3: friendSearchResult.v3,
                v4: friendSearchResult.v4,
                scene: friendSearchResult.scene,
                verifyContent: friendVerifyContent.trim(),
            }, {silent: true})
            if (response.success === false) {
                message.error(response.message || '好友申请发送失败')
                return
            }
            message.success('好友申请已发送')
            setAddFriendOpen(false)
        } catch (error) {
            message.error(error instanceof Error ? error.message : '好友申请发送失败')
        } finally {
            setAddingFriend(false)
        }
    }

    const region = (contact: Contact) => {
        const combined = [contact.province, contact.city].filter(Boolean).join(' ')
        return contact.region || combined || '-'
    }

    return (
        <section className="flex flex-col h-full bg-page">
            <PageHeader
                title="通讯录"
                action={
                    <>
                        <span className="text-sm text-muted">共 {contacts.length} 位好友</span>
                        <button
                            type="button"
                            className="btn-secondary"
                            disabled={syncing}
                            onClick={() => syncContacts()}
                        >
                            <RefreshCw size={16} className={syncing ? 'animate-spin' : ''}/>
                            {syncing ? '同步中' : '同步通讯录'}
                        </button>
                    </>
                }
            />
            <div className="flex-1 flex flex-col md:flex-row overflow-y-auto md:overflow-hidden p-3 md:p-6 gap-3 md:gap-6">
                <div className="w-full md:w-[300px] shrink-0 bg-white rounded-card shadow-card border border-hair flex flex-col h-auto md:h-full overflow-hidden">
                    <div className="p-3 border-b border-hair bg-page shrink-0">
                        <div className="flex items-center gap-2">
                            <div className="relative min-w-0 flex-1">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" size={14}/>
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    placeholder="搜索联系人"
                                    className="w-full h-8 bg-white border border-hair rounded pl-8 pr-3 text-sm text-ink placeholder:text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={openAddFriend}
                                disabled={!account}
                                title="添加好友"
                                aria-label="添加好友"
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-hair bg-white text-muted transition-colors hover:border-accent hover:text-accent disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <Plus size={17}/>
                            </button>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto divide-y divide-hair/40">
                        {filteredContacts.length ? (
                            filteredContacts.map((contact) => {
                                const isActive = selected?.wxid === contact.wxid
                                return (
                                    <button
                                        type="button"
                                        key={contact.wxid}
                                        onClick={() => setSelectedWxid(contact.wxid)}
                                        className={`flex items-center gap-3 px-3 py-3 w-full text-left transition-colors ${
                                            isActive ? 'bg-subActive text-ink' : 'hover:bg-subHover'
                                        }`}
                                    >
                                        <LazyAvatar
                                            src={contact.avatar}
                                            seed={contact.wxid}
                                            alt={displayName(contact)}
                                            className="w-10 h-10 rounded bg-gray-100 shrink-0"
                                        />
                                        <span className="text-sm text-ink truncate">{displayName(contact)}</span>
                                    </button>
                                )
                            })
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full text-muted p-6 gap-2">
                                <User size={40}/>
                                <span className="text-sm">{query ? '没有匹配的联系人' : '暂无联系人'}</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex-1 min-h-[320px] md:min-h-0 bg-white rounded-card shadow-card border border-hair flex items-center justify-center h-auto md:h-full overflow-y-auto">
                    {selected ? (
                        <div className="text-center w-full max-w-md p-10">
                            <img
                                src={avatarDisplaySrc(selected.avatar, selected.wxid)}
                                alt={displayName(selected)}
                                className="w-24 h-24 rounded mx-auto mb-4 bg-gray-100 shadow-card border border-hair"
                            />
                            <h2 className="text-2xl font-medium text-ink mb-2 flex items-center justify-center gap-2">
                                {displayName(selected)}
                                <SexIcon sex={selected.sex}/>
                            </h2>
                            {selected.remark ? (
                                <p className="text-muted mb-6">
                                    昵称: {selected.nickName || selected.name || displayName(selected)}
                                </p>
                            ) : null}
                            {(() => {
                                const items = resolveLabelNames(selected.labelIds)
                                if (!items.length) return null
                                return (
                                    <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
                                        {items.map((item) => (
                                            <span
                                                key={item.id}
                                                className="inline-flex items-center gap-1 px-2 h-6 rounded bg-green-50 text-accent text-xs"
                                            >
                                                <TagIcon size={12}/>
                                                {item.name}
                                            </span>
                                        ))}
                                    </div>
                                )
                            })()}
                            <div className="border-t border-b border-hair py-6 text-left space-y-4">
                                <div className="flex items-start">
                                    <span className="w-20 text-muted text-sm">微信号</span>
                                    <span className="text-ink text-sm flex-1 break-all">{selected.wxid}</span>
                                </div>
                                <div className="flex items-start">
                                    <span className="w-20 text-muted text-sm">地区</span>
                                    <span className="text-ink text-sm flex-1">{region(selected)}</span>
                                </div>
                                <div className="flex items-start">
                                    <span className="w-20 text-muted text-sm">手机号</span>
                                    <span className="text-ink text-sm flex-1">{selected.phone || '未填写'}</span>
                                </div>
                            </div>
                            <div className="mt-8 flex justify-center">
                                <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={() => handleOpenConversation(selected)}
                                >
                                    <MessageSquare size={16}/>
                                    发消息
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center text-muted gap-3">
                            <User size={48}/>
                            <p className="text-sm">请在左侧选择一个联系人</p>
                        </div>
                    )}
                </div>
            </div>

            {addFriendOpen ? (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="add-friend-title"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) closeAddFriend()
                    }}
                >
                    <form
                        className="w-full max-w-[440px] rounded-card border border-hair bg-white shadow-xl"
                        onSubmit={(event) => {
                            event.preventDefault()
                            void handleSearchFriend()
                        }}
                    >
                        <div className="flex items-start justify-between border-b border-hair px-6 py-5">
                            <div className="flex items-center gap-3">
                                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-green-50 text-accent">
                                    <UserPlus size={20}/>
                                </span>
                                <div>
                                    <h2 id="add-friend-title" className="text-base font-medium text-ink">添加好友</h2>
                                    <p className="mt-1 text-xs text-muted">通过微信号或手机号发送好友申请</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={closeAddFriend}
                                disabled={addingFriend || searchingFriend}
                                className="rounded p-1 text-muted transition-colors hover:bg-page hover:text-ink disabled:opacity-50"
                                aria-label="关闭"
                            >
                                <X size={18}/>
                            </button>
                        </div>
                        <div className="space-y-5 px-6 py-5">
                            <label className="block" htmlFor="add-friend-keyword">
                                <span className="mb-2 block text-sm text-soft">微信号或手机号</span>
                                <span className="flex gap-2">
                                    <input
                                        id="add-friend-keyword"
                                        aria-label="微信号或手机号"
                                        autoFocus
                                        type="text"
                                        value={friendKeyword}
                                        onChange={(event) => {
                                            setFriendKeyword(event.target.value)
                                            setFriendSearchResult(null)
                                        }}
                                        placeholder="请输入对方微信号或手机号"
                                        disabled={addingFriend || searchingFriend}
                                        className="input min-w-0 flex-1"
                                    />
                                    <button
                                        type="submit"
                                        className="btn-secondary shrink-0"
                                        disabled={addingFriend || searchingFriend || !friendKeyword.trim()}
                                    >
                                        {searchingFriend ? <Loader2 size={16} className="animate-spin"/> : <Search size={16}/>}
                                        {searchingFriend ? '搜索中' : '搜索'}
                                    </button>
                                </span>
                            </label>
                            {friendSearchResult ? (
                                <>
                                    <div className="flex items-center gap-3 rounded-lg border border-hair bg-page p-3" data-testid="friend-search-result">
                                        <LazyAvatar
                                            src={friendSearchResult.avatar}
                                            seed={friendSearchResult.wxid}
                                            alt={friendSearchResult.nickName || friendSearchResult.alias || friendSearchResult.wxid}
                                            className="h-12 w-12 shrink-0 rounded bg-gray-100"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5 text-sm font-medium text-ink">
                                                <span className="truncate">{friendSearchResult.nickName || friendSearchResult.alias || friendSearchResult.wxid}</span>
                                                <SexIcon sex={friendSearchResult.sex}/>
                                            </div>
                                            <p className="mt-1 truncate text-xs text-muted">微信号：{friendSearchResult.alias || friendSearchResult.wxid}</p>
                                            {[friendSearchResult.province, friendSearchResult.city].filter(Boolean).length ? (
                                                <p className="mt-1 truncate text-xs text-muted">
                                                    地区：{[friendSearchResult.province, friendSearchResult.city].filter(Boolean).join(' ')}
                                                </p>
                                            ) : null}
                                        </div>
                                    </div>
                                    <label className="block" htmlFor="add-friend-verification">
                                        <span className="mb-2 block text-sm text-soft">验证消息</span>
                                        <textarea
                                            id="add-friend-verification"
                                            aria-label="验证消息"
                                            value={friendVerifyContent}
                                            onChange={(event) => setFriendVerifyContent(event.target.value)}
                                            placeholder="请输入好友验证消息"
                                            rows={3}
                                            maxLength={100}
                                            disabled={addingFriend}
                                            className="textarea w-full resize-none"
                                        />
                                        <span className="mt-1 block text-right text-xs text-muted">{friendVerifyContent.length}/100</span>
                                    </label>
                                </>
                            ) : null}
                        </div>
                        <div className="flex items-center justify-end gap-3 border-t border-hair px-6 py-4">
                            <button type="button" className="btn-secondary" onClick={closeAddFriend} disabled={addingFriend || searchingFriend}>
                                取消
                            </button>
                            {friendSearchResult ? (
                                <button
                                    type="button"
                                    className="btn-primary"
                                    disabled={addingFriend}
                                    onClick={() => void handleAddFriend()}
                                >
                                    {addingFriend ? <Loader2 size={16} className="animate-spin"/> : <UserPlus size={16}/>}
                                    {addingFriend ? '发送中' : '添加好友'}
                                </button>
                            ) : null}
                        </div>
                    </form>
                </div>
            ) : null}
        </section>
    )
}
